import React from "react";

import { trackAmplitudeEvent } from "../amplitude";
import { paddleLocale, planPrices, timeLeft, twoDigits } from "../blackFriday";

const TICK_MS = 1000;
const PRO_CHECK_ATTEMPTS = 5;
const PRO_CHECK_INTERVAL_MS = 2000;
const PREVIEW_PRICES = {
  annual: { list: "$59.99", perMonth: "$2.50", sale: "$29.99" },
  lifetime: { list: "$299.99", perMonth: null, sale: null }
};

export default class BlackFridayOffer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isCheckoutUnavailable: false,
      now: Date.now(),
      prices: props.developmentPreview ? PREVIEW_PRICES : {},
      purchasing: false
    };
  }

  componentDidMount() {
    this.track("black_friday_page_viewed");
    this.timer = setInterval(() => this.setState({ now: Date.now() }), TICK_MS);

    this.loadPrices();
    this.setUpPaddle();

    const autoOpenPlan = this.props.checkoutPlan && this.plan(this.props.checkoutPlan);
    if(autoOpenPlan && autoOpenPlan.passthrough) {
      this.openCheckoutOrExplain(autoOpenPlan);
    }
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  plan(name) {
    return this.props.plans.find((plan) => plan.plan === name);
  }

  planForProductId(productId) {
    return this.props.plans.find((plan) => String(plan.productId) === String(productId));
  }

  track(eventName, properties = {}) {
    try {
      trackAmplitudeEvent(eventName, { ...this.props.tracking, ...properties });
    }
    catch(e) {
      // tracking must never block buying
    }
  }

  // The page never depends on Paddle: a missing script or a bad vendor id only disables opening checkout.
  setUpPaddle() {
    const { sandbox, vendor } = this.props;
    const vendorId = parseInt(vendor, 10);

    if(typeof Paddle === "undefined" || Number.isNaN(vendorId)) { return; }

    try {
      if(sandbox) { Paddle.Environment.set("sandbox"); }
      Paddle.Setup({ eventCallback: (data) => this.handlePaddleEvent(data), vendor: vendorId });
      this.paddleReady = true;
    }
    catch(e) {
      console.error(e);
    }
  }

  handlePaddleEvent(data) {
    const productId = data && data.eventData && data.eventData.product && data.eventData.product.id;
    const plan = this.planForProductId(productId);
    const planName = plan ? plan.plan : null;

    if(data.event === "Checkout.Loaded") {
      this.track("black_friday_checkout_started", { plan: planName });
    }
    if(data.event === "Checkout.Complete") {
      this.track("black_friday_purchase_completed", { plan: planName });
      this.setState({ purchasing: true });
      this.waitForPurchase(data.eventData.checkout && data.eventData.checkout.id, 0);
    }
  }

  // Paddle's webhook records the purchase a moment after checkout; /pro-welcome needs it to be.
  // A subscriber is already Pro, so only this checkout's purchase shows it went through.
  waitForPurchase(checkoutId, attempt) {
    if(attempt >= PRO_CHECK_ATTEMPTS) { return this.goToProWelcome(); }

    $.ajax({ data: { checkout_id: checkoutId }, url: this.props.isProSubscriberUrl })
      .done((data) => {
        if(data && data.user && data.user.checkoutProcessed) { return this.goToProWelcome(); }
        setTimeout(() => this.waitForPurchase(checkoutId, attempt + 1), PRO_CHECK_INTERVAL_MS);
      })
      .fail(() => this.goToProWelcome());
  }

  goToProWelcome() {
    window.location = this.props.proWelcomeUrl;
  }

  loadPrices() {
    const { locale, plans, sandbox } = this.props;
    const coupons = plans.map((plan) => plan.coupon).filter(Boolean).join(",");

    $.ajax({
      data: {
        calculate_recurring_discounts: true,
        coupons,
        product_ids: plans.map((plan) => plan.productId).join(",")
      },
      dataType: "jsonp",
      timeout: 5000,
      url: `https://${sandbox ? "sandbox-" : ""}checkout.paddle.com/api/2.0/prices`
    }).done((response) => {
      if(!response || !response.success) { return; }

      const prices = {};
      response.response.products.forEach((product) => {
        const plan = this.planForProductId(product.product_id);
        if(plan) {
          prices[plan.plan] = planPrices(product, { couponCode: plan.coupon, locale });
        }
      });
      this.setState({ prices });
    });
  }

  // An ad blocker can stop Paddle loading; say so rather than send the buyer round the login loop.
  openCheckoutOrExplain(plan) {
    if(this.paddleReady) {
      this.openCheckout(plan);
    }
    else {
      this.setState({ isCheckoutUnavailable: true });
    }
  }

  openCheckout(plan) {
    const { email, locale, marketingConsent } = this.props;

    Paddle.Checkout.open({
      allowQuantity: false,
      coupon: plan.coupon || undefined,
      disableLogout: true,
      email,
      locale: paddleLocale(locale),
      marketingConsent,
      passthrough: plan.passthrough,
      product: plan.productId
    });
  }

  handlePlanClick(event, plan) {
    this.track("black_friday_plan_clicked", { plan: plan.plan });

    if(this.props.developmentPreview && !this.paddleReady) {
      console.log(`[Black Friday preview] ${plan.plan} clicked, Paddle isn't set up locally`);
    }
    if(!plan.passthrough) { return; }

    event.preventDefault();
    this.openCheckoutOrExplain(plan);
  }

  renderCountdown(left) {
    const { strings } = this.props;
    const units = [
      { label: strings.days, value: left.days },
      { label: strings.hours, value: left.hours },
      { label: strings.minutes, value: left.minutes },
      { label: strings.seconds, value: left.seconds }
    ];

    return (
      <section className="bf-countdown" aria-label={strings.endsIn}>
        <span className="bf-countdown-label">{strings.endsIn}</span>
        <div className="bf-countdown-units" role="timer">
          {units.map((unit) => (
            <div className="bf-countdown-unit" key={unit.label}>
              <span className="bf-countdown-value joystix">{twoDigits(unit.value)}</span>
              <span className="bf-countdown-unit-label">{unit.label}</span>
            </div>
          ))}
        </div>
        <span className="bf-countdown-local">{this.endsAtLocalText()}</span>
      </section>
    );
  }

  endsAtLocalText() {
    const { endsAt, locale, strings } = this.props;
    const time = new Date(endsAt).toLocaleString(locale, {
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      month: "long",
      timeZoneName: "short",
      weekday: "long"
    });

    return strings.endsAtLocal.replace("%{time}", time);
  }

  renderAmount(price) {
    const { strings } = this.props;

    if(!price) {
      return <span className="bf-plan-amount bf-plan-amount-loading">{strings.priceLoading}</span>;
    }

    return <span className="bf-plan-amount">{price.sale || price.list}</span>;
  }

  renderAnnual(plan) {
    const { strings } = this.props;
    const price = this.state.prices.annual;
    // No price shows while loading, so the sale copy stays until Paddle says there's no discount.
    const isFullPrice = !!price && !price.sale;

    return (
      <article className="bf-plan bf-plan-annual" key="annual">
        {!isFullPrice && <span className="bf-plan-badge joystix">{strings.annual.badge}</span>}
        <h2 className="bf-plan-title">{strings.annual.title}</h2>
        <div className="bf-plan-price">
          {this.renderAmount(price)}
          {price && price.sale && <s className="bf-plan-was">{price.list}</s>}
          <span className="bf-plan-period">{strings.annual.perYear}</span>
        </div>
        {price && price.perMonth && (
          <span className="bf-plan-highlight">{strings.annual.perMonth.replace("%{price}", price.perMonth)}</span>
        )}
        <p className="bf-plan-body">{isFullPrice ? strings.annual.renewsWithoutDiscount : strings.annual.renews}</p>
        <a className="bf-btn bf-btn-primary" href={plan.href} onClick={(e) => this.handlePlanClick(e, plan)}>
          {isFullPrice ? strings.annual.ctaWithoutDiscount : strings.annual.cta}
        </a>
      </article>
    );
  }

  renderLifetime(plan) {
    const { strings } = this.props;
    const price = this.state.prices.lifetime;

    return (
      <article className="bf-plan" key="lifetime">
        <h2 className="bf-plan-title">{strings.lifetime.title}</h2>
        <div className="bf-plan-price">
          {this.renderAmount(price)}
          <span className="bf-plan-period">{strings.lifetime.oneTime}</span>
        </div>
        <p className="bf-plan-body">{strings.lifetime.body}</p>
        <a className="bf-btn bf-btn-primary" href={plan.href} onClick={(e) => this.handlePlanClick(e, plan)}>
          {strings.lifetime.cta}
        </a>
      </article>
    );
  }

  render() {
    const { endsAt, plans, strings } = this.props;
    const left = timeLeft(Date.parse(endsAt), this.state.now);

    if(left.isOver) {
      return <p className="bf-over" role="status">{strings.offerOver}</p>;
    }

    return (
      <div className="bf-offer">
        {this.renderCountdown(left)}
        {plans.length > 0 && (
          <section className="bf-plans">
            {plans.map((plan) => (plan.plan === "annual" ? this.renderAnnual(plan) : this.renderLifetime(plan)))}
          </section>
        )}
        {this.state.isCheckoutUnavailable && (
          <p className="bf-checkout-error" role="alert">
            <i className="fa fa-exclamation-triangle" aria-hidden="true" />
            <span>{strings.checkoutUnavailable}</span>
          </p>
        )}
        {plans.length > 0 && (
          <p className="bf-trust"><i className="fa fa-lock" aria-hidden="true" /><span>{strings.trust}</span></p>
        )}
        {this.state.purchasing && (
          <div className="bf-purchasing" role="status">
            <i className="fa fa-circle-o-notch fa-spin" aria-hidden="true" />
            <span>{strings.purchasing}</span>
          </div>
        )}
      </div>
    );
  }
}
